import type {
  CustomRoleTaxonomySelection,
  UserNode,
} from '@revelio/data-access';

import { CustomTaxonomies } from '../../../../../shared/components/RoleTaxonomyIdSelect';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
  type SingleValueFilter,
  type TreeFilter,
} from '../../../filters/types/filters';
import { buildTreeFilterNodes, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

/**
 * Extract role taxonomy ID from filters and return as string for V3 API.
 * Converts CustomRoleTaxonomySelection to numeric ID, then to string.
 * Returns "default" for default taxonomy, numeric string for custom taxonomies.
 */
export const getRoleTaxonomyId = (filters: Filter[]): string => {
  const roleTaxonomyFilter = findFilterById(filters, FilterId.ROLE_TAXONOMY);
  const taxonomySelection =
    roleTaxonomyFilter?.type === FilterType.VALUE
      ? (roleTaxonomyFilter as SingleValueFilter<CustomRoleTaxonomySelection>)
          .value
      : undefined;

  // Use same logic as V2: default to { default: true } if no filter
  const selection = taxonomySelection ?? { default: true };

  // Default taxonomy
  if ('default' in selection && selection.default) {
    return 'default';
  }

  // Handle custom taxonomies (ONET, ESCO, LIGHTCAST)
  if ('taxonomyId' in selection && typeof selection.taxonomyId === 'number') {
    const customTaxonomy = CustomTaxonomies.find(
      (t) => t.id === selection.taxonomyId
    );
    if (customTaxonomy) {
      return customTaxonomy.label.toLowerCase();
    }
    return String(selection.taxonomyId);
  }

  // For rics K50 and K10 taxonomies, use rics_k* format
  if ('ricsK50Id' in selection) {
    if (selection.ricsK50Id === '0') {
      return 'default';
    }
    return `rics_k50_${selection.ricsK50Id}`;
  }
  if ('ricsK10Id' in selection) {
    if (selection.ricsK10Id === '0') {
      return 'default';
    }
    return `rics_k10_${selection.ricsK10Id}`;
  }

  return 'default';
};

/**
 * Build role filter node from filter state.
 * Uses the generic buildTreeFilterNodes function to handle tree filter logic.
 */
export const buildRoleFilter = (filters: Filter[]): UserNode | null => {
  const roleFilter = findFilterById(filters, FilterId.ROLE);
  if (!roleFilter || roleFilter.type !== FilterType.TREE) {
    return null;
  }

  // Define levelId to V3 tag mapping
  const levelIdToTagMap: Record<string, TalentDiscoveryV3Tags> = {
    role_k7: TalentDiscoveryV3Tags.RoleK7,
    role_k150: TalentDiscoveryV3Tags.RoleK150,
    role_k1500: TalentDiscoveryV3Tags.RoleK1500,
    role_k15000: TalentDiscoveryV3Tags.RoleK15000,
    role_k17000: TalentDiscoveryV3Tags.RoleK17000,
  };

  const exprNode = buildTreeFilterNodes(roleFilter, levelIdToTagMap);
  if (!exprNode) return null;

  return wrapExprNode(exprNode, roleFilter.isNot);
};

/**
 * Build multi role filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiRoleFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiRole = findFilterById(filters, FilterId.MULTI_ROLE);

  if (!multiRole || !isMultiFilter(multiRole)) {
    return null;
  }

  if (multiRole.instances.length === 0) {
    return null;
  }

  const levelIdToTagMap: Record<string, TalentDiscoveryV3Tags> = {
    role_k7: TalentDiscoveryV3Tags.RoleK7,
    role_k150: TalentDiscoveryV3Tags.RoleK150,
    role_k1500: TalentDiscoveryV3Tags.RoleK1500,
    role_k15000: TalentDiscoveryV3Tags.RoleK15000,
    role_k17000: TalentDiscoveryV3Tags.RoleK17000,
  };

  const instanceNodes = multiRole.instances
    .filter((inst): inst is TreeFilter => inst?.type === FilterType.TREE)
    .map((instance) => {
      const exprNode = buildTreeFilterNodes(instance, levelIdToTagMap);
      if (!exprNode) return null;

      return wrapExprNode(exprNode, instance.isNot);
    })
    .filter((node): node is UserNode => node !== null);

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
